import { useState } from "react";
import { toast } from "sonner";
import { Upload, FileText, Download, Trash2, Eye, EyeOff } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { PageHeader, EmptyState, Loading } from "@/components/common";
import { UploadDialog } from "@/components/dialogs";
import { api, useFetch, errMsg, fmtDate, openFile } from "@/lib/api";

export default function Documents() {
  const { data, reload } = useFetch("/biz/documents");
  const { data: props } = useFetch("/biz/properties");
  const { data: tens } = useFetch("/biz/tenancies");
  const [open, setOpen] = useState(false);
  const act = async (fn) => { try { await fn(); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div data-testid="documents-page">
      <PageHeader eyebrow="Files" title="Documents" subtitle="Leases, certificates, inventories and invoices — stored securely and shared with renters when you choose."
        actions={<Button onClick={() => setOpen(true)} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="upload-doc-button"><Upload className="w-4 h-4 mr-1" />Upload</Button>} />
      {!data ? <Loading /> : data.length === 0 ? <EmptyState icon={FileText} title="No documents yet" text="Upload a lease or certificate to get started." /> : (
        <div className="panel p-0 overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Category</TableHead><TableHead>Linked to</TableHead><TableHead>Uploaded</TableHead><TableHead>Renter access</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {data.map((d) => (
                <TableRow key={d.id} data-testid={`doc-row-${d.id}`}>
                  <TableCell className="font-semibold">{d.name}<div className="text-xs text-slate-500 font-normal">{d.size < 1024 ? `${d.size} B` : `${Math.ceil(d.size / 1024)} KB`}</div></TableCell>
                  <TableCell className="capitalize">{d.category}</TableCell>
                  <TableCell className="text-sm">{d.property_name || "—"}{d.renter_name && <div className="text-xs text-slate-500">{d.renter_name}</div>}</TableCell>
                  <TableCell className="whitespace-nowrap">{fmtDate(d.created_at)}</TableCell>
                  <TableCell>
                    <Button size="sm" variant="ghost" onClick={() => act(() => api.patch(`/biz/documents/${d.id}/share`))} data-testid={`doc-share-${d.id}`}>
                      {d.shared_with_renter ? <><Eye className="w-4 h-4 mr-1 text-emerald-700" />Shared</> : <><EyeOff className="w-4 h-4 mr-1 text-slate-400" />Private</>}
                    </Button>
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button variant="ghost" size="icon" onClick={() => openFile(`/biz/documents/${d.id}/download`).catch((e) => toast.error(errMsg(e)))} data-testid={`doc-download-${d.id}`}><Download className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => window.confirm("Delete this document?") && act(() => api.delete(`/biz/documents/${d.id}`))} data-testid={`doc-delete-${d.id}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <UploadDialog open={open} onOpenChange={setOpen} properties={props} tenancies={tens} onSaved={reload} />
    </div>
  );
}
